import type { Category, Summary, Unit } from "../types/automation.types"

// The backend's English summary, mirrored byte for byte and checked against its
// vectors. The screens never show it: they render localized-summary.ts, whose
// English output the tests pin to this. Only UNIT_SECONDS is used at runtime.
export const UNIT_SECONDS: Record<Unit, number> = { minutes: 60, hours: 3600, days: 86400 }

const PARTS: [Unit, string][] = [
  ["days", "day"],
  ["hours", "hour"],
  ["minutes", "minute"]
]

// Compound text from total seconds, zero parts omitted: 90 minutes is
// "1 hour 30 minutes". Kept identical to the backend's apps/automation/summary.py.
export const formatDuration = (value: number, unit: Unit): string => {
  let remaining = value * UNIT_SECONDS[unit]
  const parts: string[] = []
  for (const [plural, singular] of PARTS) {
    const count = Math.floor(remaining / UNIT_SECONDS[plural])
    remaining -= count * UNIT_SECONDS[plural]
    if (count) parts.push(`${count} ${count === 1 ? singular : plural}`)
  }
  return parts.length ? parts.join(" ") : "0 minutes"
}

export interface SummaryInput {
  /** LEAD when absent, as on the backend. */
  category?: Category
  /** A Lead automation's trigger state and sender; a Nurturing rule names neither. */
  state_label?: string
  sender_email?: string
  delay_value: number
  delay_unit: Unit
  cadence_value: number | null
  cadence_unit: Unit | null
  send_count: number
}

export const describe = ({
  category = "LEAD",
  state_label,
  sender_email,
  delay_value,
  delay_unit,
  cadence_value,
  cadence_unit,
  send_count,
}: SummaryInput): Summary => {
  const delay = formatDuration(delay_value, delay_unit)
  const nurturing = category === "NURTURING"
  let sentence = nurturing
    ? `${delay} after activation, email every lead in Nurturing.`
    : `When a lead first moves to ${state_label}, send an email from ${sender_email} after ${delay}.`
  let sendText = "1 send"

  if (send_count === 1) {
    sentence += " Send once."
  } else {
    const cadence = formatDuration(cadence_value ?? 1, cadence_unit ?? "days")
    sentence += ` Send ${send_count} times, every ${cadence}.`
    sendText = `${send_count} sends · every ${cadence}`
  }

  return {
    sentence,
    steps: [
      {
        key: "trigger",
        title: "Trigger",
        text: nurturing ? "Leads in Nurturing" : `Lead first enters ${state_label}`
      },
      {
        key: "wait",
        title: "Wait",
        text: nurturing ? `${delay} after activation` : `${delay} after first entry`
      },
      { key: "send", title: "Send email", text: sendText },
    ],
  }
}
